/**
 * Thrivia Embed SDK: the single stylesheet every rendered surface uses.
 *
 * Purpose: make a Thrivia card look like a Thrivia card on a website whose CSS we have never seen
 * and cannot change. This runs in the LIGHT DOM by design (spec section 6): shadow DOM would isolate
 * us perfectly but would also hide our content from the organizer's own analytics, their print
 * styles, and every screen reader extension they rely on, and it cannot be styled by them at all.
 * Light DOM plus a namespace is the trade the spec made, so the namespace has to be airtight in both
 * directions:
 *
 *   THEIR CSS MUST NOT REACH US, up to a boundary that is stated exactly below. Every rendered
 *   element carries `thrivia-r` and the first rule is an aggressive reset scoped to it. Properties
 *   are listed rather than reached for with `all:unset` because `all` also unsets what WE set later
 *   in the same declaration block on some engines, and because a named list is what a reviewer can
 *   check against a hostile page.
 *
 *   **Where exactly the boundary is.** Every selector here is padded with `[class]`, which is a pure
 *   specificity token (an element with a class always has a class attribute, so it changes nothing
 *   about WHAT matches) and lifts the whole sheet from (0,1,0) to (0,2,0). That is the same weight as
 *   the ruled `.thrivia-r.thrivia-r` doubling and costs 7 bytes a selector instead of 10 to 13, which
 *   is why it is written this way. It has to apply to EVERY rule and not only the reset: the reset and
 *   the part rules disagree on purpose (the reset says `font:inherit`, the title says 18px) and are
 *   resolved by source order, so lifting only the reset would have inverted our own cascade and shrunk
 *   every title, invisibly, in real browsers only.
 *
 *   So: we beat `*`, `div`, `a`, `.entry-content p` and any single-class or descendant rule a theme
 *   is built from. We LOSE to **any host selector above (0,2,0)**, and that is the whole boundary,
 *   stated as a number rather than as a list of examples. It includes an id anywhere in the selector
 *   (`#content p`, exactly how many WordPress and Squarespace themes are written), three or more
 *   classes, and also the far more common **two classes plus a type** (`.entry-content .post p`, at
 *   (0,2,1)), which an earlier wording of this comment wrongly implied we won. `!important` beats us
 *   from any weight at all. That is accepted rather than fixed: winning those needs `!important` on
 *   roughly sixty declarations, which would then also have to be beaten by our own part rules.
 *
 *   **jsdom cannot see any of this.** Its `getComputedStyle` is specificity blind and importance
 *   blind, so the collision tests in `render.test.ts` prove SOURCE ORDER and nothing more. The
 *   specificity claim above is verifiable only in a real browser and is Task 8's to confirm: load the
 *   fixture with an id-scoped and a class-scoped hostile rule and read the computed styles.
 *
 *   OUR CSS MUST NOT REACH THEM. Every selector in this file begins with a `.thrivia-` class. There
 *   is exactly one rule that styles an element we did not create (`.thrivia-mounted > a`, which hides
 *   the organizer's baked permalink anchor once a real card is standing in front of it) and it is
 *   still gated on a class only we add and only while a surface is rendered.
 *
 * Branding (BRANDING_GUIDELINES.md plus its 2026-07-15 accessibility amendment):
 * - coral `#FF6F61` primary CTA with `#0B0B0B` text (7.22:1, PASS). White on coral is 2.73:1 and is
 *   explicitly forbidden by the amendment, so the legacy white-on-coral button is NOT reproduced.
 * - radii from the approved set only: 12px badges, 16px card, 50px pill CTA.
 * - muted text is `#666666` on white (5.74:1, PASS), never `#999999` (2.85:1, FAIL).
 * - the SOLD OUT badge is the mandated `#FAB5AF` chip with `#0B0B0B` text (11.52:1) rather than the
 *   legacy white-on-`rgba(255,15,15,.9)` badge. That badge is disqualified twice over: it computes to
 *   3.94:1 over white and fails AA at 10px, and because its background is 90% opaque over a COVER
 *   PHOTO its real ratio depends on the photo (measured 3.77:1 to 4.74:1 across backdrops), so it is
 *   not merely failing, it is unknowable per event. Both badges here are fully opaque for that reason.
 *   ENDED and CANCELLED use white on `#0B0B0B` (18.9:1). Deviation documented here on purpose.
 * - the disabled CTA is `#666666` on `#EEE` (4.95:1), which clears AA outright rather than relying on
 *   the WCAG 1.4.3 exemption for disabled controls.
 * - NO WEBFONTS. Poppins, Lato and Inter are named first so an organizer who already loads them gets
 *   them, and the stack falls through to the platform UI faces. Fetching a font cross-origin from an
 *   embed would add a render-blocking third-party request to somebody else's Core Web Vitals.
 *
 * Every fixed height in here is a CLS decision, not a design flourish. See `render/card.ts`.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

/**
 * Marks our injected `<style>` so it can be found, inspected and overridden by an organizer.
 *
 * A data attribute and NOT an `id`, which is not a style choice: any element carrying an id becomes
 * a named property on `window` ("named access on the Window object"), so `<style id="...">` would
 * put a second key on the organizer's global object beside `__thrivia` and break the one-global
 * contract the loader is tested against. Found with `document.querySelector("style[data-thrivia-css]")`.
 */
import { DT } from "./origin";

export const STYLE_ATTR = DT + "css";

/**
 * The stylesheet, written pre-minified because this string ships verbatim: esbuild minifies code,
 * not the contents of a string literal, so every space in here is a byte on an organizer's page.
 *
 * FIVE ONE-CHARACTER TOKENS, expanded once by {@link CSS} below. None of the five characters occurs
 * anywhere in real CSS here (there are no at-rules, no sibling combinators, no `!important`, no
 * currency and no `^=` attribute selectors), which is what makes the substitution unambiguous rather
 * than clever:
 *
 *   `@`  ->  `.thrivia-`       the namespace, on the front of EVERY selector, 40 times
 *   `~`  ->  `[class]`         the specificity pad described at the top of this file, 40 times
 *   `^`  ->  `background:`     15 times
 *   `!`  ->  `height:`         19 times, counting `line-height:`, `min-height:` and `max-height:`
 *   `$`  ->  `border-radius:`  7 times
 *
 * The counts can legitimately differ: `@` also appears where a class is named inside a selector
 * rather than at its head (`@l~ @c`, `@mounted~>a:not(@r)`), and those positions need no pad of
 * their own, while a rule can carry a doubled pad (`@c__a--o~~`) to win a specificity tie.
 *
 * It is a byte decision and it is worth restating why, since it costs a little readability: the
 * loader ships to organizer websites under a hard cap (23000 bytes since ruling R45), this sheet is
 * the single largest string in the bundle, and the five substrings above are over 1000 of its bytes.
 * It also makes every FUTURE rule cheaper, which is where the notify-me capture's rules came from.
 * Nothing about the shipped text changes: the tests read the injected sheet, after expansion, and
 * still pin that every selector begins with `.thrivia-` and weighs at least (0,2,0). The expansion
 * of this file's RAW was diffed byte for byte against the two-token version it replaced.
 *
 * THE LAST THREE TOKENS TRADE GZIP FOR RAW, DELIBERATELY, AND IT IS WORTH NAMING THE TRADE. Ruling
 * R48 made the GZIPPED figure the standing metric because that is what an organizer's visitors
 * download, but the gate that fails a build is still the raw one, and de-duplicating repeated
 * substrings is the only lever that moves raw without removing a feature or a diagnostic. It cost
 * 27 gzipped bytes (gzip was already compressing those repeats) to buy 290 raw bytes of gate
 * headroom. Report both numbers whenever this is touched again.
 *
 * Height budget for the card, which is the whole CLS story:
 *   media       padding-top 56.25% of the width, so it reserves before the image exists
 *   body        min-height 186px = 16px padding + 154px of content + 16px padding, where 154px is
 *               the MAXIMUM the card can hold: title 46 (2 lines, clamped) + date 26 + location 26
 *               + price 30 + availability 26. Every line has a fixed height, so a card with fewer
 *               lines occupies exactly the same box as the skeleton and as any other card.
 *   CTA         44px = 12 + 20 + 12, fixed.
 * The skeleton reuses these exact classes, so the reserved box and the final box are the same box
 * by construction rather than by two numbers someone has to keep in sync.
 */
const RAW =
  "@r~,@r~ *{box-sizing:border-box;position:static;display:block;" +
  "margin:0;padding:0;border:0;^0 0;font:inherit;color:inherit;text-align:left;" +
  "text-decoration:none;text-transform:none;letter-spacing:normal;width:auto;max-width:none}" +
  "@r~{width:100%;font-family:Poppins,Lato,Inter,system-ui,-apple-system,Arial," +
  "sans-serif;font-size:14px;color:#333}" +
  "@r~ script{display:none}" +
  "@r~ a:focus{outline:2px solid #0B0B0B}" +
  // Hides the ORGANIZER'S baked permalink anchor once a surface is standing in front of it, and
  // `:not(@r)` is the whole of a Critical Task 8 found in a real browser. Without it this rule is
  // `.thrivia-mounted[class] > a` at (0,2,1), and the BUTTON surface's own pill is a direct `<a>`
  // child of the mount carrying `.thrivia-b[class]` at (0,2,0). The hide rule outweighed it, so
  // every `data-thrivia-mode="button"` mount rendered a pill with `display:none` AND a baked
  // anchor with `display:none`: measured mount height 0, nothing visible at all, on the SDK's
  // headline snippet. Cards and lists were unaffected because their CTA is nested inside
  // `.thrivia-c` rather than being a child of the mount, which is why nothing else showed it.
  // jsdom cannot see this: its getComputedStyle is specificity blind, so the existing tests
  // asserted the rule EXISTS and could never assert what it beat.
  // `:not(@r)` says what the rule always meant - hide THEIR anchor, never ours - and every node
  // this bundle renders carries `thrivia-r`. It also lifts the selector to (0,3,1), so it still
  // beats a host stylesheet by the same margin as before.
  "@mounted~>a:not(@r){display:none}" +
  "@c~{max-width:420px;^#FFF;border:1px solid #E5E7EB;$16px;" +
  "overflow:hidden}" +
  "@c__m~{position:relative;padding-top:56.25%;^#EEE}" +
  "@c__i~{position:absolute;inset:0;width:100%;!100%;object-fit:cover}" +
  "@c__b~,@c__s~{$12px;font-size:10px;font-weight:800;" +
  "line-!14px;letter-spacing:.5px;text-transform:uppercase}" +
  "@c__b~{position:absolute;top:12px;left:12px;padding:4px 8px;^#0B0B0B;" +
  "color:#FFF}" +
  "@c__b--s~{^#FAB5AF;color:#0B0B0B}" +
  "@c__y~{padding:16px;min-!186px}" +
  // min-height AND max-height: the clamp caps the text at two lines on engines that support it, the
  // max-height caps the BOX on engines that do not, so the reserved height holds either way.
  "@c__t~{min-!46px;max-!46px;font-size:18px;font-weight:700;" +
  "line-!23px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;" +
  "-webkit-box-orient:vertical}" +
  "@c__d~{!20px;margin-top:6px;line-!20px;color:#666;overflow:hidden;" +
  "white-space:nowrap;text-overflow:ellipsis}" +
  "@c__n~{color:#0B0B0B;font-weight:700}" +
  "@c__p~{!22px;margin-top:8px;font-size:16px;font-weight:700;line-!22px}" +
  "@c__s~{display:inline-block;margin-top:8px;padding:2px 8px;^#FAB5AF;" +
  "color:#0B0B0B}" +
  "@c__a~{!44px;margin:0 16px 16px;padding:12px 16px;$50px;" +
  "^#FF6F61;color:#0B0B0B;font-size:16px;font-weight:700;line-!20px;" +
  "text-align:center;cursor:pointer}" +
  "@c__a~:hover{^#FE423F}" +
  // The optional secondary action (an itinerary PDF, a brochure), outlined so the primary CTA
  // keeps the only filled button on the card. It reuses `@c__a`'s box, so a card that has one is
  // exactly one button taller than a card that does not, rather than a different shape.
  "@c__a--2~~{^#FFF;color:#0B0B0B;border:1px solid #0B0B0B;margin-bottom:8px}" +
  // `[class][class]`, and the duplicate is the entire fix. A pseudo-class carries class-level
  // weight, so the hover rule above is (0,3,0) while a single `[class]` here is (0,2,0): hovering a
  // DISABLED sold-out chip repainted it active coral under #666 text, which is 1.66:1 and the worst
  // contrast in the bundle. Duplicating the attribute selector ties the weight at (0,3,0), and
  // source order (this rule is later) then keeps the chip grey. Seven bytes; no new selector.
  "@c__a--o~~{^#EEE;color:#666;cursor:not-allowed}" +
  // The notify-me capture, in the slot between the card body and its action.
  //
  // The submit button carries its OWN class rather than reusing `@c__a`. Reuse would have been
  // about 90 bytes cheaper and would have made `.thrivia-c__a` match two different controls on one
  // card, so any selector meaning "the card's action" (ours, and an organizer's) would have started
  // matching the notify button first. The declarations are copied; the meaning is not shared.
  //
  // Contrast, against BRANDING_GUIDELINES.md plus the 2026-07-15 amendment: #0B0B0B on #FF6F61 for
  // the submit (7.22:1, and never white on coral), #666 on white for the consent line (5.74:1, the
  // muted colour this sheet already uses and never #999), #0B0B0B for the status line (18.9:1)
  // because it carries the one thing the visitor most needs to read, and the same measured #666 on
  // #EEE (4.95:1) as the disabled card CTA while a submit is in flight. Controls are 44px, matching
  // the card CTA, so the tap target on a phone is the one the rest of the card already uses.
  // THE TRIGGER'S BOX IS THE LOW-SPOTS CHIP'S BOX, and that equality is the CLS contract rather
  // than a coincidence: `@c__s` occupies 8px of margin plus an 18px chip, this occupies 2px of
  // margin plus a 24px control, and both therefore consume exactly the 26px the body's 186px
  // min-height reserves for a line that a sold out card never draws. Changing either number
  // without the other makes every sold out card taller than the skeleton painted for it, which is
  // a layout shift on somebody else's page; `render.test.ts` pins the two sums against each other.
  // 24px is also the WCAG 2.5.8 minimum target size, and the control is full width, so the tap
  // target is the width of the card rather than the width of the words.
  "@c__cap-t~{margin-top:2px;width:100%;font-weight:700;line-!24px;color:#FE423F;cursor:pointer}" +
  "@c__cap~{margin:0 16px 12px}" +
  "@c__cap-r~{display:flex;gap:8px}" +
  // `min-width:0` is what stops a flex input from refusing to shrink below its intrinsic width and
  // pushing the button out of a 320px card.
  "@c__cap-i~{flex:1;min-width:0;!44px;padding:0 12px;border:1px solid #E5E7EB;" +
  "$12px;^#FFF}" +
  "@c__cap-b~{!44px;padding:0 16px;$50px;^#FF6F61;color:#0B0B0B;font-weight:700;cursor:pointer}" +
  // No hover rule on this button, unlike the card's CTA, and that is a byte decision taken in the
  // open: it costs a colour change on a device with a pointer, it is invisible on the phones most
  // of these cards are read on, and dropping it also lets the disabled rule hold its ground with a
  // single specificity pad instead of the doubled one a `:hover` would have outweighed.
  "@c__cap-b~:disabled{^#EEE;color:#666;cursor:default}" +
  // The consent line and the status line. The status one carries its margin unconditionally: an
  // empty `<p>` has no line box, so all it costs while silent is 8px inside a form the visitor
  // opened on purpose, which is cheaper than the `:not(:empty)` rule that bought those 8px back.
  "@c__cap-c~{margin-top:8px;font-size:11px;line-!15px;color:#666}" +
  "@c__cap-m~{margin-top:8px;font-size:12px;line-!16px;color:#0B0B0B}" +
  // The standalone button surface: the same pill outside a card box, so it drops the card's
  // gutters and sizes to its own label instead of filling the mount.
  "@b~{display:inline-block;width:auto;margin:0}" +
  // Declared last on purpose: the skeleton reuses `.thrivia-c__a` and `.thrivia-c__t` for their box
  // metrics, so its grey has to win on source order over the coral CTA it is standing in for.
  "@c__bar~{^#EEE;$12px;color:transparent}" +
  "@l~{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));" +
  "gap:16px}" +
  "@l~ @c{max-width:none}" +
  // The checkout overlay: two rules, and the close control is styled inline in `overlay.ts` rather
  // than costing a third. The z-index clears a sticky header and a cookie banner and is deliberately
  // FINITE, below the 32 bit maximum, so an organizer can still put something over it if they must.
  "@o~{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;" +
  "justify-content:center;padding:16px;^rgba(11,11,11,.72)}" +
  "@o__s~{position:absolute;inset:0;margin:auto;width:24px;!24px;pointer-events:none;" +
  "border:3px solid #E5E7EB;border-top-color:#FF6F61;$50%}" +
  "@o__f~{width:100%;max-width:480px;!100%;max-!760px;" +
  "$16px;^#FFF}";

/**
 * The shipped stylesheet text, tokens expanded.
 *
 * Expanded once at bundle evaluation rather than per injection: the result is a constant, and a
 * page that re-injects after a Turbo navigation must not pay for it twice.
 */
const TOKENS: Record<string, string> = {
  "@": ".thrivia-",
  "~": "[class]",
  "^": "background:",
  "!": "height:",
  "$": "border-radius:",
};

export const CSS = RAW.replace(/[@~^!$]/g, (token) => TOKENS[token]);

/**
 * The `<style>` we injected, or null.
 *
 * A live element reference rather than a boolean, because a boolean cannot see the stylesheet LEAVE.
 * Turbo, Swup, Barba and every other "morph the document" navigation library replaces `<head>` on a
 * route change, which drops our sheet: with a flag the loader would never inject again and the
 * organizer would be left with permanently unstyled cards AND a duplicate visible permalink anchor
 * under each one, because `.thrivia-mounted > a` went with it. Checking `isConnected` on the element
 * itself costs one property read per render and repairs the page on the next paint.
 */
let sheet: HTMLStyleElement | null = null;

/**
 * Rules added at runtime by a lazily loaded surface, appended to {@link CSS}.
 *
 * A satellite bundle cannot ship its own `<style>`: two sheets means two injection points to
 * repair when a Turbo navigation replaces `<head>`, and the second one would silently stop being
 * repaired the moment anybody forgot it. Appending to the one sheet keeps the repair in exactly
 * one place, and keeps the source order that several rules in this file depend on.
 */
let extra = "";

/**
 * Injects the stylesheet, once per document and again if the document loses it.
 *
 * Deliberately lazy: a page carrying only a button mount, or no mount at all, never pays for CSS it
 * does not use, and nothing is written to the organizer's `<head>` until we are actually about to
 * draw something.
 *
 * Guest code, so it swallows its own failures. A host that has replaced `createElement` or frozen
 * `<head>` costs the visitor our styling, which leaves readable unstyled content, and costs their
 * page nothing at all. `sheet` is cleared on the way out of the catch so a transient failure is
 * retried on the next paint rather than remembered forever.
 */
export function addCss(text: string): void {
  if (extra.indexOf(text) >= 0) return;
  extra += text;
  // Rewrite in place when the sheet is already up; `injectStyles` reads `extra` on the way in, so
  // a sheet that has not been injected yet needs nothing more than the injection it was going to
  // get anyway.
  if (sheet && sheet.isConnected) {
    try {
      sheet.textContent = CSS + extra;
    } catch {
      sheet = null;
    }
  }
}

export function injectStyles(): void {
  if (sheet && sheet.isConnected) return;
  try {
    const style = document.createElement("style");
    style.setAttribute(STYLE_ATTR, "");
    // textContent, never innerHTML. The rule is absolute in this bundle even where the input is our
    // own constant, because "we only use innerHTML for safe strings" is exactly how the unsafe one
    // gets added later.
    style.textContent = CSS + extra;
    // `document.head` and not a `documentElement` fallback: every HTML document a browser parses has
    // a head, created implicitly even when the markup omits it, and the only documents without one
    // are XML documents that cannot execute our script tag in the first place.
    document.head.appendChild(style);
    sheet = style;
  } catch {
    sheet = null;
  }
}
